import ComponentManager from "../../componentFramework";
import GeographicMap from "../GeographicMap";
import EventListenerManager from "../EventListenerManager";
import JsfJsCommand from "../JsfJsCommand";

/**
 * To connect this component to a GeographicMap instance set the "data-geographicMapControls-mapContainerQuery"
 * to a query to find a common ancestor to the GeographicMap you want to connect to.
 * The value of that attribute will be ran using closest() on this component's root element
 * and then will find the first instance of ".geographicMap" to connect this component to.
 */
@ComponentManager.register()
export default class GeographicMapControls {
	public static componentStyleClass = "geographicMapControls";

	private readonly eventListenerManager = new EventListenerManager();
	private readonly latInput: HTMLInputElement;
	private readonly longInput: HTMLInputElement;

	// While these fields are actually initialized in the constructor. Unfortunately, TypeScript is unable to detect it.
	private toFieldMetric!: (v: number) => number;
	private toMapMetric!: (v: number) => number;

	private geographicMap?: GeographicMap;

	constructor(private readonly rootElement: HTMLElement) {
		this.latInput = rootElement.querySelector(".geographicMapControls__latitude") as HTMLInputElement;
		if (this.latInput !== null) {
			const latInputManaged = this.eventListenerManager.manage(this.latInput);
			latInputManaged.addEventListener("input", this.onPositionValueInput);
			latInputManaged.addEventListener("change", this.onPositionValueChange);
		}

		this.longInput = rootElement.querySelector(".geographicMapControls__longitude") as HTMLInputElement;
		if (this.longInput !== null) {
			const longInputManaged = this.eventListenerManager.manage(this.longInput);
			longInputManaged.addEventListener("input", this.onPositionValueInput);
			longInputManaged.addEventListener("change", this.onPositionValueChange);
		}

		const metricInput = rootElement.querySelector(".geographicMapControls__metric") as HTMLSelectElement;
		if (metricInput !== null) {
			const metricInputManaged = this.eventListenerManager.manage(metricInput);
			metricInputManaged.addEventListener("input", this.onPositionValueInput);
			metricInputManaged.addEventListener("change", this.onMetricValueChange);
			this.applyMetricName(metricInput.value);
		} else {
			this.applyMetricName(null);
		}

		const geoMap = this.getGeographicMap();
		if (geoMap !== null) {
			geoMap.registerControls(this);
		}
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventListenerManager.removeListeners();
		this.geographicMap = undefined;
	}

	private applyMetricName(metricName: string | null) {
		if (metricName === "radian") {
			this.toFieldMetric = v => v * Math.PI / 180.0;
			this.toMapMetric = v => v * 180.0 / Math.PI;
		} else {
			this.toFieldMetric = v => v;
			this.toMapMetric = v => v;
		}
	}

	private onMetricValueChange = (event: Event) => {
		let lat = parseFloat(this.latInput.value);
		if (!Number.isNaN(lat)) {
			lat = this.toMapMetric(lat);
		}
		let long = parseFloat(this.longInput.value);
		if (!Number.isNaN(long)) {
			long = this.toMapMetric(long);
		}
		this.applyMetricName((event.currentTarget as HTMLSelectElement).value);
		if (!Number.isNaN(lat)) {
			this.latInput.value = this.toFieldMetric(lat).toString();
		}
		if (!Number.isNaN(long)) {
			this.longInput.value = this.toFieldMetric(long).toString();
		}
		this.save();
	}

	private onPositionValueInput = () => {
		this.updateMap();
	}
	private onPositionValueChange = () => {
		this.updateMap();
		this.save();
	}

	private updateMap() {
		const geoMap = this.getGeographicMap();
		if (geoMap !== null) {
			let lat = parseFloat(this.latInput.value);
			if (Number.isNaN(lat)) {
				lat = 0;
			}
			let long = parseFloat(this.longInput.value);
			if (Number.isNaN(long)) {
				long = 0;
			}
			geoMap.setPosition(this.toMapMetric(lat), this.toMapMetric(long));
		}
	}

	public setLatitude(lat: number) {
		this.latInput.value = this.toFieldMetric(lat).toString();
	}
	public setLongitude(long: number) {
		this.longInput.value = this.toFieldMetric(long).toString();
	}

	public save() {
		JsfJsCommand.run("geographicMapControlsSave", this.rootElement);
	}

	private getGeographicMap(): GeographicMap|null {
		if (this.geographicMap === undefined) {
			const mapContainerQuery = this.rootElement.getAttribute("data-geographicMapControls-mapContainerQuery");
			if (mapContainerQuery === null) {
				return null;
			}
			const geoMapContainerElem = this.rootElement.closest(mapContainerQuery);
			if (geoMapContainerElem === null) {
				return null;
			}
			const geoMapElem = geoMapContainerElem.querySelector("." + GeographicMap.componentStyleClass);
			if (geoMapElem === null) {
				return null;
			}
			const geoMap = GeographicMap.getInstanceFor(geoMapElem as HTMLElement);
			if (geoMap !== undefined && geoMap !== null) {
				this.geographicMap = geoMap;
			} else {
				return null;
			}
		}
		return this.geographicMap;
	}
}
